'use strict';

const api = require('../../helpers/api');
const helper = require('../../helpers/helper');
const loaderInstance = require('../../components/loader');
const clientSideValidation = require('base/components/clientSideValidation');

const $loaderContainer = document.querySelector('.js-paypalLoader');
const amount = parseFloat(document.querySelector('.grand-total-sum').textContent.trim().replace(/[^0-9.-]/g, ''));
const isOrderZeroAmount = !isNaN(amount) && amount === 0;
const alertTemplates = document.querySelectorAll('.js-alert-template');

let visibleAlertTemplate;

alertTemplates.forEach(alertTemplate => {
    if (!alertTemplate.classList.contains('d-none')) {
        visibleAlertTemplate = alertTemplate;
    }
});

const CreditCardBaseModel = require('./creditCardBase');
const AlertHandlerModel = require('.././alertHandler');
const alertHandler = new AlertHandlerModel();

/**
 * The class represents the hosted fields functionality
 */
class HostedFields extends CreditCardBaseModel {
    constructor() {
        super();

        this.CSS_CLASSES = {
            D_NONE: 'd-none',
            BORDER_DANGER: 'border-danger',
            ACTIVE: 'active'
        };

        this.hostedFieldsSelectors = {
            number: '#paypalCardNumber',
            cvv: '#paypalCvv',
            expirationDate: '#paypalExpirationDate'
        };

        this.isPayNowFlowEnabled = window.paypalPreferences.isPayNowFlowEnabled;
        this.hostedFieldsConfigs = helper.tryParseJSON(document.querySelector('.js-paypal-cc-fields').getAttribute('data-configs'));
        this.loader = loaderInstance($loaderContainer);
        this.$hostedFieldContainer = document.querySelector('.js-paypal-cc-fields');
        this.$continueButton = document.querySelector('button.submit-payment');
        this.is3DSEnabled = this.hostedFieldsConfigs.threeDSecureValue !== 'DISABLED';
        this.$cardHolderInput = document.getElementById('paypalCardOwner');
        this.customerEmail = document.querySelector('.customer-summary-email').textContent.trim();
        this.$cardHolderInput.style.fontSize = this.hostedFieldsConfigs.fieldsConfig.styles.fontSize + 'pt';
    }

    /**
     * Initiates and process the hosted fields
     */
    init() {
        this.renderMark();

        paypal.HostedFields.render({
            styles: {
                input: {
                    'font-size': this.hostedFieldsConfigs.fieldsConfig.styles.fontSize + 'pt',
                    color: this.hostedFieldsConfigs.fieldsConfig.styles.color
                },
                '.number': {
                    'font-family': 'helvetica, tahoma, calibri, sans-serif'
                },
                '.valid': {
                    color: this.hostedFieldsConfigs.fieldsConfig.styles.validColor
                },
                '.invalid': {
                    color: this.hostedFieldsConfigs.fieldsConfig.styles.invalidColor
                }
            },
            fields: {
                number: {
                    selector: this.hostedFieldsSelectors.number,
                    placeholder: this.hostedFieldsConfigs.fieldsPlaceholders.number
                },
                cvv: {
                    selector: this.hostedFieldsSelectors.cvv,
                    placeholder: this.hostedFieldsConfigs.fieldsPlaceholders.cvv
                },
                expirationDate: {
                    selector: this.hostedFieldsSelectors.expirationDate,
                    placeholder: this.hostedFieldsConfigs.fieldsPlaceholders.expirationDate
                }
            },
            createOrder: () => {
                const $saveCreditCardAccount = document.getElementById('saveCreditCardAccount');

                const paymentSourceData = {
                    card: {
                        name: this.cardHolderName,
                        billing_address: {
                            address_line_1: this.billingAddress.address1,
                            address_line_2: this.billingAddress.address2,
                            admin_area_2: this.billingAddress.city,
                            admin_area_1: this.billingAddress.stateCode,
                            postal_code: this.billingAddress.postalCode,
                            country_code: this.billingAddress.countryCode
                        },
                        attributes: {}
                    }
                };

                if (this.isPayNowFlowEnabled) {
                    paymentSourceData.billingAddressPayNow = {
                        name: `${this.billingAddress.firstName} ${this.billingAddress.lastName}`,
                        phone: {
                            phone_number: {
                                national_number: this.billingAddress.phone
                            }
                        },
                        email_address: this.customerEmail,
                        address: Object.assign({}, paymentSourceData.card.billing_address)
                    };
                }

                if ($saveCreditCardAccount && $saveCreditCardAccount.checked) {
                    paymentSourceData.card.attributes.vault = {
                        store_in_vault: 'ON_SUCCESS'
                    };
                }

                const orderIdData = api.getPaypalOrderId(paymentSourceData);

                if (orderIdData.error && orderIdData.error === window.paypalConstants.ZERO_AMOUNT_ERROR_MESSAGE) {
                    return alertHandler.showError(orderIdData.errorMessage);
                }

                return orderIdData.id;
            }
        })
            .then(cardFields => {
                this.cardFields = cardFields;

                this.$continueButton.addEventListener('click', event => {
                    if (!event.isTrusted || !this.isActiveCreditCardTab()) {
                        return;
                    }

                    if (isOrderZeroAmount && visibleAlertTemplate) {
                        event.preventDefault();
                        event.stopPropagation();
                        alertHandler.showError(window.paypalConstants.ZERO_AMOUNT_ERROR_MESSAGE);
                    }

                    this.hideErrors();
                    this.loader.show();

                    const isNewCcFlow = !this.$paypalCreditCardList
                        || (this.$paypalCreditCardList && this.$paypalCreditCardList.selectedOptions[0].id === 'new-card-account');

                    if (isNewCcFlow) {
                        event.preventDefault();
                        event.stopPropagation();

                        this.processNewCreditCard();
                    }
                });
            });

        this.$cardHolderInput.addEventListener('input', (event) => {
            const validateSymbol = !(/[0-9]/i.test(event.data));

            event.target.style.color = this.hostedFieldsConfigs.fieldsConfig.styles.color;

            if (!validateSymbol) {
                event.target.value = event.target.value.slice(0, -1);
            }
        });
    }

    /**
     * Process a new Credit card on the checkout page
     */
    processNewCreditCard() {
        const $billingForm = document.getElementById('dwfrm_billing');
        const getAddressFieldsFromUI = require('base/checkout/address').methods.getAddressFieldsFromUI;

        clientSideValidation.functions.clearForm($billingForm);

        const isValidForm = helper.validateForm($billingForm);

        if (!isValidForm.isValid) {
            this.loader.hide();

            return;
        }

        this.billingAddress = getAddressFieldsFromUI($billingForm);

        this.cardHolderName = this.$cardHolderInput.value;

        const submitObject = {
            cardholderName: this.cardHolderName
        };

        this.cardFields
            .submit(submitObject)
            .then(({ authenticationReason }) => {
                if (authenticationReason === window.paypalConstants.CC_3DS_AUTHENTICATION_REASON_SKIPPED_BY_BUYER) {
                    this.loader.hide();

                    return alertHandler.showError(this.hostedFieldsConfigs.errorMessages.threeDSVerificationFailed);
                }

                const fieldsState = this.cardFields.getState();

                Object.assign(fieldsState.fields, { cardholderName: this.cardHolderName });

                return api.validateHostedFields(fieldsState)
                    .then(response => response.json())
                    .then(data => {
                        this.loader.hide();

                        if (data.error) {
                            const errorObj = {
                                name: 'INVALID_REQUEST',
                                details: [],
                                errorNotification: data.errorNotification
                            };

                            Object.values(data.fieldsErrors).forEach(field => {
                                errorObj.details.push({
                                    field: field.fieldName, description: field.errorMessage
                                });
                            });

                            return this.handleErrors(errorObj);
                        }

                        if (this.isPayNowFlowEnabled) {
                            const SessionStorageModel = require('../sessionStorage');
                            const sessionStorageInstance = new SessionStorageModel();

                            api.finishPayNowPayment(window.paypalConstants.PAYMENT_METHOD_ID_PAYPAL_CREDIT_CARD);

                            return sessionStorageInstance.clear();
                        }

                        $('body').on('checkout:updateCheckoutView', () => this.clearHostedFields());

                        return this.$continueButton.click();
                    });
            })
            .catch((err) => {
                this.handleErrors(err);
                this.loader.hide();
            });
    }

    /**
     * Clears hosted fields
     */
    clearHostedFields() {
        Object.keys(this.hostedFieldsSelectors).forEach(selectorName => {
            this.cardFields.clear(selectorName);
        });
    }

    /**
     * Renders the credit card tab
     */
    renderMark() {
        paypal.Marks({
            fundingSource: 'card'
        }).render('.js-credit-card-mark');

        document.querySelector('.js-nav-item-credit-card').classList.remove(this.CSS_CLASSES.D_NONE);
    }

    /**
     * Indicates whether the credit card tab is active
     * @returns {boolean} True/False
     */
    isActiveCreditCardTab() {
        return document.getElementById('credit-card-content').classList.contains(this.CSS_CLASSES.ACTIVE);
    }

    // Base hosted fields error handler
    handleErrors(error) {
        if (['INVALID_REQUEST', 'UNPROCESSABLE_ENTITY'].includes(error.name)) {
            error.details.forEach((elem) => {
                const fieldPathAsArray = elem.field.split('/');

                this.showError(fieldPathAsArray.pop(), elem.description);
            });

            alertHandler.showError(this.hostedFieldsConfigs.fieldsGeneralNotificationError);
        } else if (isOrderZeroAmount && visibleAlertTemplate) {
            alertHandler.showError(window.paypalConstants.ZERO_AMOUNT_ERROR_MESSAGE);
        }
    }

    /**
     * Shows the hosted fields errors
     * @param {string} field A field name
     * @param {string} message An error message
     */
    showError(field, message) {
        const $errorContainer = document.querySelector(`.js-card-${field}-error`);
        const $elementContainer = document.querySelector(`.js-card-${field}-input`);

        $errorContainer.innerText = message;
        $errorContainer.classList.remove(this.CSS_CLASSES.D_NONE);
        $elementContainer.classList.add(this.CSS_CLASSES.BORDER_DANGER);

        $elementContainer.style.color = this.hostedFieldsConfigs.fieldsConfig.styles.invalidColor;
    }

    /**
     * Hides the hosted fields errors
     */
    hideErrors() {
        const $errorContainers = document.querySelectorAll('.js-card-error');
        const $inputContainers = document.querySelectorAll('.js-card-input');

        $errorContainers.forEach(element => {
            element.classList.add(this.CSS_CLASSES.D_NONE);
            element.innerText = '';
        });

        $inputContainers.forEach(element => {
            element.style.color = this.hostedFieldsConfigs.fieldsConfig.styles.color;
            element.classList.remove(this.CSS_CLASSES.BORDER_DANGER);
        });
    }

    /**
     * Shows a hosted fields container
     */
    showHostedFields() {
        this.$hostedFieldContainer.classList.remove(this.CSS_CLASSES.D_NONE);

        this.loader.hide();
    }

    /**
     * Hides a hosted fields container
     */
    hideHostedFields() {
        this.$hostedFieldContainer.classList.add(this.CSS_CLASSES.D_NONE);
    }
}

module.exports = HostedFields;
